import { Edit, Tag, Trash2 } from "lucide-react"

import type { ProductResponseDto } from "@/api/api-client"
import { Button } from "@/components/ui/button"

import type { InventoryLabels, InventoryProductCallbacks } from "./inventory-types"

interface InventoryProductActionsProps extends InventoryProductCallbacks {
  product: ProductResponseDto
  labels: Pick<InventoryLabels, "manageBatches" | "edit" | "delete" | "deleting" | "deleteConfirm">
  variant: "comfortable" | "dense"
  isDeleting: boolean
}

export function InventoryProductActions({
  product,
  labels,
  variant,
  isDeleting,
  onManageBatches,
  onEdit,
  onDelete,
}: InventoryProductActionsProps) {
  const handleDelete = () => {
    if (confirm(labels.deleteConfirm)) onDelete(product.id)
  }

  if (variant === "dense") {
    return (
      <div className="col-span-2 flex justify-end gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-10 w-10 rounded-none transition-colors hover:bg-accent hover:text-accent-foreground"
          onClick={() => onManageBatches(product)}
          title={labels.manageBatches}
        >
          <Tag className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="primary-white"
          className="h-10 w-10"
          onClick={() => onEdit(product)}
          title={labels.edit}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="destructive-white"
          className="h-10 w-10"
          onClick={handleDelete}
          title={labels.delete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <Button
        className="btn-industrial h-14 bg-foreground text-lg text-background"
        onClick={() => onManageBatches(product)}
      >
        <Tag className="mr-3 h-5 w-5" />
        {labels.manageBatches}
      </Button>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="primary-white" className="h-14" onClick={() => onEdit(product)}>
          <Edit className="h-4 w-4" />
          <span className="text-xs uppercase">{labels.edit}</span>
        </Button>
        <Button
          variant="destructive-white"
          className="h-14"
          disabled={isDeleting}
          onClick={handleDelete}
        >
          {isDeleting ? (
            <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          <span className="ml-2 text-xs uppercase">
            {isDeleting ? labels.deleting : labels.delete}
          </span>
        </Button>
      </div>
    </div>
  )
}
